import React, { useState } from 'react';
import RichTextContent from './RichTextContent';
import {
  createPendingFileRef,
  getFileRefDocumentId,
  isPendingFormFileRef,
  isStoredFormFileRef,
  normalizeFormFileAnswerList
} from '../../utils/formFileUploads';
import './formsRichText.css';

function formatFileSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeFileList(value, allowMultiple) {
  return normalizeFormFileAnswerList(value, allowMultiple);
}

const FormField = ({
  field,
  value,
  onChange,
  error,
  variant = 'inline',
  hideLabel = false,
  readOnly = false,
  onPendingFileRegister,
  onPendingFileRelease,
  documentUrlById = {}
}) => {
  const questionId = field.question_id;
  const isCanvas = variant === 'canvas';
  const [isFocused, setIsFocused] = useState(false);
  
  const handleChange = (e) => {
    if (readOnly) return;
    if (!questionId) {
      console.error(`Field "${field.label}" is missing question_id`);
      return;
    }
    onChange(questionId, e.target.value);
  };

  const canvasInputProps = isCanvas
    ? { className: 'afc-control', disabled: readOnly }
    : {
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        style: {
          ...styles.input,
          ...(isFocused ? styles.inputFocused : styles.inputUnfocused)
        }
      };

  const emitChange = (qid, val) => {
    if (readOnly) return;
    onChange(qid, val);
  };

  const renderFileUploadList = (allowMultiple, isCanvas) => {
    const files = normalizeFileList(value, allowMultiple);

    const handleFilesSelected = (fileList) => {
      if (!questionId) return;
      const selected = Array.from(fileList || []);
      if (selected.length === 0) return;

      if (allowMultiple) {
        const next = [...files];
        selected.forEach((file) => {
          const ref = createPendingFileRef(file);
          onPendingFileRegister?.(ref.localKey, file);
          next.push(ref);
        });
        emitChange(questionId, next);
      } else {
        const existing = files[0];
        if (isPendingFormFileRef(existing)) {
          onPendingFileRelease?.(existing.localKey);
        }
        const file = selected[0];
        const ref = createPendingFileRef(file);
        if (isStoredFormFileRef(existing)) {
          ref.replacesDocumentId = existing.documentId;
        }
        onPendingFileRegister?.(ref.localKey, file);
        emitChange(questionId, ref);
      }
    };

    const removeFileAt = (fileIndex) => {
      if (!questionId) return;
      const target = files[fileIndex];
      if (isPendingFormFileRef(target)) {
        onPendingFileRelease?.(target.localKey);
      }
      if (allowMultiple) {
        emitChange(
          questionId,
          files.filter((_, idx) => idx !== fileIndex)
        );
      } else {
        emitChange(questionId, null);
      }
    };

    const renderFileRow = (file, fileIndex) => {
      const displayName = file.fileName || file.name || 'File';
      const documentId = getFileRefDocumentId(file);
      const href =
        file.url ||
        (documentId && documentUrlById[documentId]) ||
        null;
      const pending = isPendingFormFileRef(file);
      const isStored = isStoredFormFileRef(file);

      const nameEl = href && !pending ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={isCanvas ? 'afc-file-link' : undefined}
          style={isCanvas ? undefined : styles.fileLink}
        >
          {displayName}
        </a>
      ) : (
        <span>{displayName}{pending ? ' (unsaved)' : ''}</span>
      );

      if (isCanvas) {
        return (
          <li
            key={`${file.localKey || documentId || file.name}-${fileIndex}`}
            className={`afc-file-item${readOnly && isStored ? ' afc-file-item--stored' : ''}`}
          >
            {readOnly && isStored ? <span className="afc-file-icon" aria-hidden>📎</span> : null}
            {nameEl}
            {file.size ? <span className="afc-file-meta">{formatFileSize(file.size)}</span> : null}
            {!readOnly ? (
              <button
                type="button"
                className="afc-file-remove"
                onClick={() => removeFileAt(fileIndex)}
                aria-label={`Remove ${displayName}`}
              >
                ×
              </button>
            ) : null}
          </li>
        );
      }

      return (
        <li
          key={`${file.localKey || documentId || file.name}-${fileIndex}`}
          style={styles.fileListItem}
        >
          {readOnly && isStored ? <span style={styles.fileIcon} aria-hidden>📎</span> : null}
          {nameEl}
          {file.size ? <span style={styles.fileMeta}>{formatFileSize(file.size)}</span> : null}
          {!readOnly ? (
            <button
              type="button"
              style={styles.fileRemove}
              onClick={() => removeFileAt(fileIndex)}
              aria-label={`Remove ${displayName}`}
            >
              ×
            </button>
          ) : null}
        </li>
      );
    };

    if (isCanvas) {
      return (
        <div className={`afc-file-zone${readOnly ? ' afc-file-zone--readonly' : ''}`}>
          {!readOnly ? (
            <input
              type="file"
              multiple={allowMultiple}
              disabled={readOnly}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                e.target.value = '';
              }}
            />
          ) : null}
          {files.length > 0 ? (
            <ul className="afc-file-list">{files.map(renderFileRow)}</ul>
          ) : readOnly ? (
            <p className="afc-file-hint">No file uploaded.</p>
          ) : (
            <p className="afc-file-hint">
              {allowMultiple ? 'Choose one or more files' : 'Choose a file'}
            </p>
          )}
        </div>
      );
    }

    return (
      <div style={{ ...styles.fileUpload, ...(readOnly ? styles.fileUploadReadonly : null) }}>
        {!readOnly ? (
          <input
            type="file"
            multiple={allowMultiple}
            onChange={(e) => {
              handleFilesSelected(e.target.files);
              e.target.value = '';
            }}
          />
        ) : null}
        {files.length > 0 ? (
          <ul style={styles.fileList}>{files.map(renderFileRow)}</ul>
        ) : readOnly ? (
          <p style={styles.fileHint}>No file uploaded.</p>
        ) : (
          <p style={styles.fileHint}>
            {allowMultiple ? 'Choose one or more files' : 'Choose a file'}
          </p>
        )}
      </div>
    );
  };

  const renderInput = () => {
    switch (field.type) {
      case 'varchar':
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            {...canvasInputProps}
          />
        );
      
      case 'email':
        return (
          <input
            type="email"
            value={value || ''}
            onChange={handleChange}
            {...canvasInputProps}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={handleChange}
            {...canvasInputProps}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={handleChange}
            {...canvasInputProps}
          />
        );
      
      case 'boolean':
        return isCanvas ? (
          <label className="afc-boolean">
            <input
              type="checkbox"
              checked={value || false}
              disabled={readOnly}
              onChange={(e) => {
                if (!questionId) return;
                emitChange(questionId, e.target.checked);
              }}
            />
            <RichTextContent html={field.label} className="forms-field-label-rich forms-field-label-rich--inline" />
            {field.required ? <span className="forms-field-required"> *</span> : null}
          </label>
        ) : (
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => {
                if (!questionId) return;
                emitChange(questionId, e.target.checked);
              }}
              style={styles.checkbox}
            />
            <RichTextContent html={field.label} className="forms-field-label-rich forms-field-label-rich--inline" />
            {field.required ? <span className="forms-field-required"> *</span> : null}
          </label>
        );
      
      case 'single-choice':
        return (
          <div className={isCanvas ? 'afc-choice-list' : undefined} style={isCanvas ? undefined : styles.checkboxGroup}>
            {field.options?.map((option, index) => (
              <label
                key={index}
                className={isCanvas ? 'afc-choice-option' : undefined}
                style={isCanvas ? undefined : styles.checkboxLabel}
              >
                <input
                  type="checkbox"
                  name={questionId}
                  value={option}
                  checked={value === option}
                  disabled={readOnly}
                  onChange={() => {
                    if (!questionId) return;
                    if (value === option) {
                      emitChange(questionId, '');
                    } else {
                      emitChange(questionId, option);
                    }
                  }}
                  style={isCanvas ? undefined : styles.checkbox}
                />
                <span style={isCanvas ? undefined : styles.checkboxText}>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'multiple-choice': {
        const selected = Array.isArray(value) ? value : value ? [value] : [];
        return (
          <div className={isCanvas ? 'afc-choice-list' : undefined} style={isCanvas ? undefined : styles.checkboxGroup}>
            {field.options?.map((option, index) => (
              <label
                key={index}
                className={isCanvas ? 'afc-choice-option' : undefined}
                style={isCanvas ? undefined : styles.checkboxLabel}
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={selected.includes(option)}
                  disabled={readOnly}
                  onChange={() => {
                    if (!questionId) return;
                    const next = selected.includes(option)
                      ? selected.filter((v) => v !== option)
                      : [...selected, option];
                    emitChange(questionId, next);
                  }}
                  style={isCanvas ? undefined : styles.checkbox}
                />
                <span style={isCanvas ? undefined : styles.checkboxText}>{option}</span>
              </label>
            ))}
          </div>
        );
      }
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={handleChange}
            className={isCanvas ? 'afc-control afc-select' : undefined}
            disabled={readOnly}
            style={
              isCanvas
                ? undefined
                : {
                    ...styles.select,
                    ...(isFocused ? styles.inputFocused : styles.inputUnfocused)
                  }
            }
            onFocus={isCanvas ? undefined : () => setIsFocused(true)}
            onBlur={isCanvas ? undefined : () => setIsFocused(false)}
          >
            <option value=""></option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={handleChange}
            rows={isCanvas ? 5 : 4}
            className={isCanvas ? 'afc-control afc-textarea' : undefined}
            disabled={readOnly}
            style={
              isCanvas
                ? undefined
                : {
                    ...styles.textarea,
                    ...(isFocused ? styles.inputFocused : styles.inputUnfocused)
                  }
            }
            onFocus={isCanvas ? undefined : () => setIsFocused(true)}
            onBlur={isCanvas ? undefined : () => setIsFocused(false)}
          />
        );

      case 'file-upload': {
        const allowMultiple = field.allowMultiple !== false;
        return renderFileUploadList(allowMultiple, isCanvas);
      }
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            {...canvasInputProps}
          />
        );
    }
  };

  const showLabel = !hideLabel && field.type !== 'boolean';

  return (
    <div className={isCanvas ? 'afc-field' : undefined} style={isCanvas ? undefined : styles.fieldContainer}>
      {showLabel && (
        <div className="forms-field-label-wrap">
          <RichTextContent html={field.label} className="forms-field-label-rich" />
          {field.required ? <span className="forms-field-required"> *</span> : null}
        </div>
      )}
      {renderInput()}
      {error && !isCanvas ? <div style={styles.error}>{error}</div> : null}
      {error && isCanvas ? <div className="afc-field-error">{error}</div> : null}
    </div>
  );
};

const styles = {
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '15px',
    paddingBottom: '4px',
    borderBottom: '1px solid #e5e7eb'
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  required: {
    color: '#ef4444'
  },
  input: {
    width: '100%',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
  },
  inputUnfocused: {
    background: 'transparent',
    border: 'none',
    padding: '2px 0',
    color: '#111827',
    minHeight: '20px'
  },
  inputFocused: {
    background: 'white',
    border: '1px solid #3b82f6',
    borderRadius: '4px',
    padding: '6px 10px',
    color: '#111827'
  },
  select: {
    width: '100%',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    backgroundColor: 'transparent'
  },
  textarea: {
    width: '100%',
    fontSize: '0.95rem',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#64748b',
    fontWeight: '400'
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: '#3b82f6'
  },
  checkboxText: {
    userSelect: 'none'
  },
  checkboxGroup: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  error: {
    color: '#ef4444',
    fontSize: '14px',
    marginTop: '4px'
  },
  fileUpload: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  fileUploadReadonly: {
    marginTop: '4px'
  },
  fileIcon: {
    flexShrink: 0,
    fontSize: '0.9rem',
    lineHeight: 1
  },
  fileInput: {
    fontSize: '0.85rem',
    color: '#475569'
  },
  fileHint: {
    margin: 0,
    fontSize: '0.8rem',
    color: '#94a3b8'
  },
  fileList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  fileListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#334155'
  },
  fileMeta: {
    fontSize: '0.75rem',
    color: '#94a3b8'
  },
  fileLink: {
    color: 'var(--color-brand, #1c85fc)',
    textDecoration: 'none'
  },
  fileRemove: {
    marginLeft: 'auto',
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '1rem',
    lineHeight: 1,
    padding: '0 4px'
  }
};

export default FormField;
